import { FormGroupHeader } from "@/components/shared";
import { ChatMessage, FetchUsersOnTenderQuery, FetchUsersOnTenderQueryVariables, UserOnEntity, FetchUsersOnTenderDocument } from "@/graphql/__generated__/graphql";
import { useUserData } from "@/hooks/cache";
import { useFetchChatMessagesByEntityIdPolling } from "@/hooks/queries";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext, useParams } from "react-router-dom";
import ChatBubble from "./ChatBubble";
import ChatInput from "./ChatInput";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CircleLoader } from "@/components/shared/loaders";
import { cn } from "@/lib/utils";
import { useQuery } from "@apollo/client/react";

/**
 * Main component for the tender chat page
 * Contains layout and fetching of chat messages
 * @returns JSX.Element
 */
function ChatComponent() {

  // Hooks
  const { t } = useTranslation(["components"]);
  const { tenderByIdData } = useOutletContext<EditTenderContext>();
  const { tenderID } = useParams<{ tenderID: string }>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasInitiallyLoadedRef = useRef<boolean>(false);
  const user = useUserData();

  // Query hooks
  const { data, loading } = useFetchChatMessagesByEntityIdPolling(tenderID as string);
  const { data: usersOnTenderData } = useQuery<FetchUsersOnTenderQuery, FetchUsersOnTenderQueryVariables>(FetchUsersOnTenderDocument, {
    variables: {
      tenderId: Number(tenderID)
    }
  });

  const tenderUsers = usersOnTenderData?.fetchUsersOnTender || [];
  const chatMessages = data?.fetchChatMessagesByEntityId;

  // Track when initial load completes
  useEffect(() => {
    if (!loading && data !== undefined) {
      hasInitiallyLoadedRef.current = true;
    }
  }, [loading, data]);

  // Scroll to bottom of chat messages
  // FIXME: Currently smoothly scrolls to bottom, need to change to instantly at the bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [chatMessages]);

  return (
    <div className="flex flex-col gap-2 h-full pb-[110px]">
      <FormGroupHeader
        className="mb-0 pr-4 pl-2"
        title={`${tenderByIdData.fetchTenderById.description.name} ${t("chat.title")}`}
      />
      {/* TODO: Depending on use case and scale, might need to implement virtual scrolling */}
      <ScrollArea className="grow pb-2 pr-4 pl-2 min-h-0">
        <div className="space-y-4">
          {
            chatMessages && chatMessages?.length > 0 ? (
              chatMessages.map((chatMessage) => {
                const isCurrentUserMessage = chatMessage.user?.id === user?.id;

                return (
                  <div key={`chat-message-${chatMessage.id}`} className={cn("flex", isCurrentUserMessage ? "justify-end" : "justify-start")}>
                    <ChatBubble chatMessage={chatMessage as ChatMessage} isCurrentUserMessage={isCurrentUserMessage} tenderUsers={tenderUsers as UserOnEntity[]} />
                  </div>
                );
              }) ) : loading && !hasInitiallyLoadedRef.current ? (
              <div className="flex items-center justify-center h-[50vh]">
                <CircleLoader
                  visible={true}
                  height={40}
                  width={40}
                  strokeWidth={4}
                  ariaLabel="oval-loading"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-[50vh]">
                <p className="text-sm text-gray-400">
                  {t("chat.nodata")}
                </p>
              </div>
            )
          }
          {/* div for anchor to auto scroll to bottom */}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>
      <ChatInput tenderID={tenderID} tenderUsers={tenderUsers as UserOnEntity[]} />
    </div>
  );
}

export default ChatComponent;
